'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { LuArrowRight } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import SafeImage from '@/core/components/safe-image'
import { cn } from '@/core/utils/common'
import { useViewer } from '@/features/viewer/hooks/use-viewer'

import { selectVehicle } from '../actions/vehicle'
import ProductPrice from './product-price'

import type { Route } from 'next'
import type { FinderData } from '../services/catalog.server'

export default function VehicleFinder({ data }: { data: FinderData }) {
  const { viewer } = useViewer()
  const saved = data.vehicles.find(
    (vehicle) => vehicle.id === viewer?.vehicle?.id
  )
  const makersWithModels = useMemo(
    () =>
      data.makers.filter((maker) =>
        data.vehicles.some(
          (vehicle) => vehicle.makerCode === maker.code && vehicle.count > 0
        )
      ),
    [data]
  )
  const [picked, setPicked] = useState<{
    makerCode: string
    vehicleId: string
  } | null>(null)
  const makerCode =
    picked?.makerCode ?? saved?.makerCode ?? makersWithModels[0]?.code ?? ''
  const vehicleId = picked ? picked.vehicleId : (saved?.id ?? '')

  const models = useMemo(
    () => data.vehicles.filter((vehicle) => vehicle.makerCode === makerCode),
    [data.vehicles, makerCode]
  )
  const selected = models.find((vehicle) => vehicle.id === vehicleId)

  return (
    <div>
      <div
        role="tablist"
        aria-label="제조사"
        className="scrollbar-none -mx-1 flex gap-1 overflow-x-auto border-b border-border"
      >
        {makersWithModels.map((maker) => {
          const active = maker.code === makerCode
          return (
            <Button
              key={maker.code}
              type="button"
              role="tab"
              variant="ghost"
              aria-selected={active}
              onClick={() =>
                setPicked({ makerCode: maker.code, vehicleId: '' })
              }
              className={cn(
                'shrink-0 rounded-none border-b-2 px-3 text-c15 hover:bg-transparent over10:px-4 over10:text-c16',
                active
                  ? 'border-foreground font-bold'
                  : 'border-transparent font-medium text-description hover:text-foreground'
              )}
            >
              {maker.name}
            </Button>
          )
        })}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {models.map((vehicle) => {
          const active = vehicle.id === vehicleId
          return (
            <Button
              key={vehicle.id}
              type="button"
              variant={active ? 'primary' : 'outline'}
              disabled={vehicle.count === 0}
              aria-pressed={active}
              onClick={() =>
                setPicked({
                  makerCode,
                  vehicleId: active ? '' : vehicle.id,
                })
              }
              className="min-h-11 rounded-full px-4 text-c15 over10:min-h-12 over10:px-5 over10:text-c16"
            >
              {vehicle.label}
              <span
                className={cn(
                  'text-c13 tabular-nums',
                  active ? 'text-inverse/80' : 'text-description'
                )}
              >
                {vehicle.count > 0 ? vehicle.count : '준비중'}
              </span>
            </Button>
          )
        })}
      </div>

      {selected && selected.preview.length > 0 && (
        <div key={selected.id} className="animate-fade-up mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="type-title">
              {selected.label}에 맞는 제품 {selected.count}개
            </p>
            <form action={selectVehicle}>
              <input type="hidden" name="vehicleId" value={selected.id} />
              <Button type="submit" variant="secondary">
                전체 보기 · 내 차로 저장
                <LuArrowRight aria-hidden="true" className="size-4" />
              </Button>
            </form>
          </div>
          <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-5 over8:grid-cols-4 over10:gap-x-5">
            {selected.preview.map((product) => (
              <li key={product.id}>
                <Link
                  href={`/products/${product.id}` as Route}
                  className="group block"
                >
                  <span className="relative block aspect-square overflow-hidden rounded-panel bg-surface-soft">
                    {product.imageUrl && (
                      <SafeImage
                        src={product.imageUrl}
                        alt=""
                        fill
                        sizes="(min-width: 801px) 25vw, 50vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    )}
                  </span>
                  <span className="mt-2.5 line-clamp-2 block text-c15 leading-[1.45] group-hover:underline over10:text-c16">
                    {product.name}
                  </span>
                  <ProductPrice
                    className="mt-1"
                    price={product.price}
                    listPrice={product.listPrice}
                    discountRate={product.discountRate}
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
